import { logout as apiLogout } from '@/api/auth'
import { configureClient } from '@/api/client'
import type { TokenPair } from '@/api/types'
import { ledgerStorage, refreshStorage } from '@/auth/storage'

/**
 * Сессия живёт **вне React** — модульным стором с подпиской.
 *
 * Не состоянием и не ref'ами по двум причинам. Первая: ротация refresh происходит на каждом
 * обновлении access, и класть токены в состояние значит перерисовывать всё дерево на каждый
 * фоновый рефреш. Вторая: клиент читает токен в момент запроса, а запрос уходит из обработчика
 * события — то есть не в рендере, где ref'ы читать нельзя.
 *
 * Наружу торчит один булев признак «есть сессия», за ним React и следит.
 */

let accessToken: string | null = null
let refreshToken: string | null = refreshStorage.read()

const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Снимок — примитив: `useSyncExternalStore` сравнивает по ссылке, и объект зациклил бы рендер. */
export function hasSession(): boolean {
  return refreshToken !== null
}

export function signIn(pair: TokenPair): void {
  accessToken = pair.accessToken
  refreshToken = pair.refreshToken
  refreshStorage.write(pair.refreshToken)
  emit()
}

/** Забыть сессию локально, ничего не спрашивая у сервера: вызывается и когда сервер её уже отозвал. */
export function forget(): void {
  accessToken = null
  refreshToken = null
  refreshStorage.clear()
  ledgerStorage.clear()
  emit()
}

export async function signOut(): Promise<void> {
  const token = refreshToken
  forget()
  if (token) await apiLogout(token)
}

let installed = false

/**
 * Связать стор с клиентом. Вызывается из `main.tsx` до первого рендера.
 *
 * Явно, а не побочным эффектом импорта: порядок побочных эффектов зависит от того, кто кого
 * импортировал первым, и запрос, ушедший до связывания, потерял бы токен.
 */
export function installSession(): void {
  if (installed) return
  installed = true
  configureClient({
    getAccessToken: () => accessToken,
    getRefreshToken: () => refreshToken,
    onTokenRefreshed: signIn,
    onSessionExpired: forget,
  })
}
